import React, { useEffect, useRef } from 'react';
import { useHistoryBackClose } from '../../hooks/useHistoryModal';

interface BaseModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal content */
  children: React.ReactNode;
  /** Footer content (buttons, etc.) */
  footer?: React.ReactNode;
  /** Modal size — wide 는 폼+갤러리를 2단으로 한 화면에 담는 상세 모달용 */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full' | 'wide';
  /** Whether to close on ESC key */
  closeOnEsc?: boolean;
  /** Whether to close on backdrop click */
  closeOnBackdrop?: boolean;
  /** Additional class for modal content */
  className?: string;
  /** Whether to show close button in header */
  showCloseButton?: boolean;
  /** Enter 키 시 호출 (primary action). textarea 입력 줄바꿈은 제외. */
  onEnter?: () => void;
  /** 브라우저 뒤로가기로 modal 닫기. 기본 활성 — 닫지 않아야 하는 modal 만 false. */
  historyBackClose?: boolean;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-4xl',
  wide: 'max-w-6xl',
};

/**
 * Base modal component with standardized structure
 * - Fixed positioning with backdrop overlay
 * - Header with title and close button
 * - Scrollable content area
 * - Optional footer for actions
 * - ESC key and backdrop click handlers
 */
const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnEsc = true,
  closeOnBackdrop = true,
  className = '',
  showCloseButton = true,
  onEnter,
  historyBackClose = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // 뒤로가기-닫기: 비활성 시 isOpen 을 항상 false 로 넘겨 no-op.
  useHistoryBackClose(historyBackClose && isOpen, onClose);

  /*
    핸들러를 ref 로 고정한다 — 호출부가 onEnter/onClose 에 인라인 화살표 함수를 넘기는 것이 일반적이라,
    이를 effect dep 에 두면 매 렌더마다 리스너 재등록 + body.overflow 를 ''↔'hidden' 으로 왕복한다.
    중첩 모달에서는 안쪽 모달의 cleanup 이 바깥 모달이 살아있는 채로 body 스크롤을 풀어버린다.
  */
  const onCloseRef = useRef(onClose);
  const onEnterRef = useRef(onEnter);
  onCloseRef.current = onClose;
  onEnterRef.current = onEnter;

  // ESC=닫기, Enter=primary(onEnter). textarea 줄바꿈은 보호.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === 'Escape') {
        onCloseRef.current();
      } else if (onEnterRef.current && e.key === 'Enter' && (e.target as HTMLElement)?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        onEnterRef.current();
      }
    };
    document.addEventListener('keydown', handleKey);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeOnEsc]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    /*
      모달 위치·여백은 화면 너비와 무관하게 동일하다 — 항상 중앙, 상하 대칭.
      외부 여백은 오버레이 패딩이 단일 소스이고(safe-area 를 하한으로 흡수),
      최대 높이는 전 구간 85dvh 고정이라 브레이크포인트 경계에서 튀지 않는다.
    */
    <div
      className="fixed inset-0 z-50 bg-gray-600 bg-opacity-50 flex items-center justify-center overscroll-contain px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className={`bg-white shadow-xl w-full ${sizeClasses[size] || sizeClasses.full} overflow-hidden flex flex-col rounded-lg max-h-[85dvh] ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white px-4 py-3 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="text-base font-medium text-gray-900">{title}</h3>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>

        {/* Footer - optional. 중앙 정렬이라 하단이 화면 끝에 닿지 않으므로 safe-area 는 오버레이 패딩이 담당 */}
        {footer && (
          <div className="sticky bottom-0 bg-white px-4 py-3 border-t flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default BaseModal;

// Export commonly used modal footer layouts
export const ModalFooter: React.FC<{
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right' | 'between';
}> = ({ children, align = 'right' }) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };

  return (
    <div className={`flex items-center space-x-2 ${alignClasses[align]}`}>
      {children}
    </div>
  );
};
